// Standard cycling power metrics (Coggan / TrainingPeaks).

const ROLLING_WINDOW = 30;
const NP_TIME_HOLD_MAX_S = 2.0;

function parseSampleTime(raw) {
  if (!raw) return null;
  const ms = new Date(raw).getTime();
  return Number.isFinite(ms) ? ms : null;
}

function computeNormalizedPowerOnSeries(series) {
  if (!series || series.length < ROLLING_WINDOW) return null;
  const raised = [];
  for (let i = ROLLING_WINDOW - 1; i < series.length; i += 1) {
    const window = series.slice(i - ROLLING_WINDOW + 1, i + 1);
    if (window.some((v) => v == null)) continue;
    const avg = window.reduce((a, b) => a + b, 0) / window.length;
    raised.push(avg ** 4);
  }
  if (!raised.length) return null;
  const mean = raised.reduce((a, b) => a + b, 0) / raised.length;
  return Math.round(mean ** 0.25);
}

export function buildHeld1HzPowerSeries(samples, maxHoldS = NP_TIME_HOLD_MAX_S) {
  const rows = [];
  for (const sample of samples || []) {
    const t = parseSampleTime(sample?.t);
    const power = sample?.power;
    if (t == null || power == null || power < 0 || Number.isNaN(power)) continue;
    rows.push({ t, power: Number(power) });
  }
  if (!rows.length) return [];
  rows.sort((a, b) => a.t - b.t);
  const start = rows[0].t;
  const totalSec = Math.max(0, Math.round((rows[rows.length - 1].t - start) / 1000));
  const series = Array.from({ length: totalSec + 1 }, () => null);
  let sampleIndex = 0;
  for (let offset = 0; offset <= totalSec; offset += 1) {
    const slotT = start + offset * 1000;
    while (sampleIndex + 1 < rows.length && rows[sampleIndex + 1].t <= slotT) {
      sampleIndex += 1;
    }
    const ageS = (slotT - rows[sampleIndex].t) / 1000;
    if (ageS >= 0 && ageS <= maxHoldS) series[offset] = rows[sampleIndex].power;
  }
  return series;
}

export function computeNormalizedPower(powers) {
  const clean = powers.filter((p) => p != null && p >= 0);
  return computeNormalizedPowerOnSeries(clean);
}

export function computeNormalizedPowerFromSamples(samples) {
  const hasTime = (samples || []).some((sample) => parseSampleTime(sample?.t) != null);
  if (hasTime) return computeNormalizedPowerOnSeries(buildHeld1HzPowerSeries(samples));
  return computeNormalizedPower((samples || []).map((sample) => sample?.power));
}

/** Intensity Factor = NP / FTP */
export function computeIntensityFactor(np, ftp) {
  if (!np || !ftp) return null;
  return Math.round((np / ftp) * 1000) / 1000;
}

/** Training Stress Score */
export function computeTSS(durationSeconds, np, ftp) {
  if (!durationSeconds || !np || !ftp) return null;
  const intensityFactor = np / ftp;
  return Math.round((durationSeconds * np * intensityFactor) / (ftp * 3600) * 100);
}

/** Power-to-heart-rate efficiency (NP / avg HR). */
export function computeEfficiencyFactor(np, avgHr) {
  if (!np || !avgHr) return null;
  return Math.round((np / avgHr) * 100) / 100;
}

export function computePowerMetrics(samples, durationSeconds, ftp) {
  const powers = samples.map((s) => s.power);
  const hrs = samples.map((s) => s.hr).filter((h) => h != null);
  const avgHr = hrs.length
    ? Math.round(hrs.reduce((a, b) => a + b, 0) / hrs.length)
    : null;

  const np = computeNormalizedPowerFromSamples(samples);
  const intensityFactor = computeIntensityFactor(np, ftp);
  const tss = computeTSS(durationSeconds, np, ftp);
  const ef = computeEfficiencyFactor(np, avgHr);

  return {
    normalized_power: np,
    intensity_factor: intensityFactor,
    tss,
    efficiency_factor: ef,
  };
}

export function resolveActivityPowerMetrics(activity, userFtp = null) {
  if (!activity?.has_power) return null;

  const effectiveFtp = activity.effective_ftp_watts ?? userFtp ?? null;
  if (activity.normalized_power != null) {
    return {
      normalized_power: activity.normalized_power,
      intensity_factor:
        activity.intensity_factor ??
        computeIntensityFactor(activity.normalized_power, effectiveFtp),
      tss:
        activity.tss ??
        computeTSS(activity.duration_seconds, activity.normalized_power, effectiveFtp),
      efficiency_factor: null,
    };
  }

  const samples = activity.samples || [];
  const hasPowerSamples = samples.some((s) => s.power != null && s.power >= 0);
  if (!hasPowerSamples) return null;

  const np = computeNormalizedPowerFromSamples(samples);
  if (np == null) return null;

  return {
    normalized_power: np,
    intensity_factor: computeIntensityFactor(np, effectiveFtp),
    tss: computeTSS(activity.duration_seconds, np, effectiveFtp),
    efficiency_factor: null,
  };
}

export function fmtIF(if_) {
  if (if_ == null) return "—";
  return `${Math.round(if_ * 100)}%`;
}

export function fmtEF(ef) {
  if (ef == null) return "—";
  return ef.toFixed(2);
}
